import { ApiItemType } from '@highimpact-frontend/api-types';
import {
  ConfirmModal,
  CustomizedDialog,
  GenericErrorBoundary,
  NavigationTabBar,
  type TabInfo,
} from '@highimpact/dashboard-ui';
import { PermissionDecorator } from '@highimpact/react-decorators';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { forwardRef, type LegacyRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getDashboardsForSiteByType,
  getHomepageDashboardsByType,
} from '../api/siteDashboardIndex';
import { useDashboardHost } from '../context/DashboardHostContext';
import DashboardFormComponent from '../forms/DashboardFormComponent';
import ReportDashboardFormComponent from '../forms/ReportDashboardFormComponent';
import { useSiteDashboardIndex } from '../hooks/useSiteDashboardIndex';
import { useModal } from '../internal/hooks/useModal';
import { useSubmit } from '../internal/hooks/useSubmit';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../runtime/DashboardLibraryProvider';
import {
  setDashboardForm,
  updateDashboardName,
} from '../state/dashboard/slice';

interface BaseProps {
  gridType: 'grids' | 'reports';
  gridId: number;
  simplifiedView?: boolean;
  itemNameCreateBtn?: ApiItemType;
  itemNameDeleteBtn?: ApiItemType;
  itemNameEditEl?: ApiItemType;
}

interface TabBarForSiteProps extends BaseProps {
  mode: 'forsite';
  projectId: number;
  siteId: number;
}

interface ContextFreeTabBarProps extends BaseProps {
  mode: 'contextfree';
}

type DasboardTabBarProps = TabBarForSiteProps | ContextFreeTabBarProps;

function DashboardTabBarComponent(
  {
    gridType,
    gridId,
    simplifiedView,
    itemNameCreateBtn,
    itemNameDeleteBtn,
    itemNameEditEl,
    ...props
  }: DasboardTabBarProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const dispatch = useDashboardLibraryDispatch();
  const host = useDashboardHost();

  const navigateToDashboard = (id: number) => {
    host.navigate(
      gridType === 'grids'
        ? host.buildDashboardUrl(id)
        : host.buildReportUrl(id)
    );
  };
  const {
    data: siteIdsToDashboardIds,
    loading,
    error,
    reload,
  } = useSiteDashboardIndex();

  const { t } = useTranslation('dashboards');

  const { submit } = useSubmit();
  const deleteModal = useModal<
    {
      message: string;
      target: string;
    },
    'confirmed' | 'cancelled'
  >();

  const dashboardFormState = useDashboardLibrarySelector(
    (state) => state.dashboard.dashboardForm
  );

  const dashboardFormType =
    gridType === 'grids' ? 'UserDashboardFormOpen' : 'ReportDashboardFormOpen';

  const canCreateReport = host.report?.enabled !== false;

  if (loading) {
    return (
      <div ref={ref}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error || !siteIdsToDashboardIds) {
    return (
      <div ref={ref}>
        <Typography color="error">
          {t('Impossible de charger la liste des tableaux de bord')}
        </Typography>
      </div>
    );
  }

  const deleteDashboard = async (
    dashboardId: number,
    dashboardName: string,
    dashboardsForSiteAndGridType: { id: number }[]
  ) => {
    await submit({
      callbacks: [
        async () => host.api.dashboard.deleteDashboard(dashboardId),
        () => reload(),
        () => {
          const nextId =
            dashboardsForSiteAndGridType.find((info) => info.id !== dashboardId)
              ?.id ?? 0;
          navigateToDashboard(nextId);
        },
      ],
      errorMessages: {
        anyError: t(
          `Une erreur est survenue lors de la suppression du tableau de bord`,
          { dashboardName: dashboardName }
        ),
        unicityError: t(`Le tableau de bord a déjà été supprimé`, {
          dashboardName: dashboardName,
        }),
      },
      successMessage: t(`Le tableau de bord a été supprimé`, {
        dashboardName: dashboardName,
      }),
    });
  };

  const targetDashboardType =
    props.mode === 'contextfree'
      ? 'homepage_dashboard'
      : gridType === 'grids'
      ? 'user_dashboard'
      : 'report_dashboard';
  const displayableDashboards =
    props.mode === 'forsite'
      ? getDashboardsForSiteByType(
          siteIdsToDashboardIds,
          props.siteId,
          targetDashboardType
        )
      : getHomepageDashboardsByType(siteIdsToDashboardIds, targetDashboardType);
  const displayableDasboardsForSelectedType = [...displayableDashboards];

  const currentTabIndex =
    displayableDasboardsForSelectedType.findIndex(({ id }) => id === gridId) ??
    0;

  const tabInfo: TabInfo[] = displayableDasboardsForSelectedType.map(
    (dashboardInfo, index) => ({
      name: dashboardInfo.name,
      onClick: () => navigateToDashboard(dashboardInfo.id),
      renameable: currentTabIndex === index,
      deletable: currentTabIndex === index,
      onRename: async (newName) => {
        return host.api.dashboard
          .renameDashboard(dashboardInfo.id, {
            name: newName,
          })
          .then(() => {
            dispatch(
              updateDashboardName({
                dashboardId: dashboardInfo.id,
                name: newName,
                gridType: gridType,
              })
            );
            void reload();
          });
      },
      onDelete: async () => {
        const result = await deleteModal.openAndAwaitResponse({
          message: t('Voulez-vous supprimer le tableau de bord'),
          target: dashboardInfo.name,
        });

        if (result === 'cancelled') return;

        await deleteDashboard(
          dashboardInfo.id,
          dashboardInfo.name,
          displayableDasboardsForSelectedType
        );
      },
    })
  );

  const shouldShowTabs =
    Boolean(simplifiedView) === false || tabInfo.length > 1;

  const handleNewTab = (): void => {
    if (gridType === 'reports' && !canCreateReport) {
      return;
    }
    dispatch(setDashboardForm(dashboardFormType));
  };

  const showNewTabButton =
    props.mode === 'forsite' && (gridType === 'grids' || canCreateReport);

  return (
    <div ref={ref}>
      <GenericErrorBoundary>
        <PermissionDecorator
          item="dashboards_list"
          project_id={props.mode === 'forsite' ? props.projectId : undefined}
          site_id={props.mode === 'forsite' ? props.siteId : undefined}
        >
          {shouldShowTabs && (
            <NavigationTabBar
              currentTabIndex={
                displayableDasboardsForSelectedType.findIndex(
                  ({ id }) => id === gridId
                ) ?? 0
              }
              tabs={tabInfo}
              onNew={showNewTabButton ? handleNewTab : undefined}
              simplifiedView={simplifiedView}
              itemNameCreateBtn={itemNameCreateBtn}
              itemNameDeleteBtn={itemNameDeleteBtn}
              itemNameEditEl={itemNameEditEl}
              projectId={props.mode === 'forsite' ? props.projectId : undefined}
              siteId={props.mode === 'forsite' ? props.siteId : undefined}
            />
          )}
          {dashboardFormState === 'UserDashboardFormOpen' &&
            props.mode === 'forsite' && (
              <DashboardFormComponent
                title={t('Nouveau tableau de bord')}
                isOpen={true}
                project_id={props.projectId}
                site_id={props.siteId}
                onDashboardCreated={({
                  dashboard_id,
                  site_id: _site_id,
                  dashboardName: _dashboardName,
                }) => {
                  dispatch(setDashboardForm('DashboardFormClosed'));
                  void reload();
                  navigateToDashboard(dashboard_id);
                }}
                onCancel={() =>
                  dispatch(setDashboardForm('DashboardFormClosed'))
                }
              />
            )}

          {dashboardFormState === 'ReportDashboardFormOpen' &&
            props.mode === 'forsite' &&
            canCreateReport && (
              <CustomizedDialog
                title="Nouveau rapport"
                open={true}
                onCancel={() => {
                  dispatch(setDashboardForm('DashboardFormClosed'));
                }}
                dialogMaxWidth="800px"
              >
                <ReportDashboardFormComponent
                  onDashboardCreated={({ dashboard_id, site_id: _site_id }) => {
                    dispatch(setDashboardForm('DashboardFormClosed'));
                    void reload();
                    navigateToDashboard(dashboard_id);
                  }}
                  onCancel={() =>
                    dispatch(setDashboardForm('DashboardFormClosed'))
                  }
                />
              </CustomizedDialog>
            )}
          {deleteModal.isOpen && (
            <ConfirmModal
              onCancel={() => deleteModal.resolveResponse('cancelled')}
              onConfirm={() => deleteModal.resolveResponse('confirmed')}
              open={deleteModal.isOpen}
              title="Confirmation"
            >
              <p>
                {deleteModal.params?.message ?? ''}{' '}
                <Typography fontWeight="bold" component="span">
                  {deleteModal.params?.target}
                </Typography>{' '}
                ?
              </p>
            </ConfirmModal>
          )}
        </PermissionDecorator>
      </GenericErrorBoundary>
    </div>
  );
}

export default forwardRef(DashboardTabBarComponent);
